import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, DollarSign } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Currencies = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    currencyid: null,
    currencyname: '',
    symbol: '',
    monetaryunit: '',
    noofdecimals: 2,
    countryid: '',
    statusbasecurrency: false,
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchCountries();
    fetchCurrencies();
  }, []);

  useEffect(() => {
    filterCurrencies();
  }, [searchTerm, currencies]);

  const fetchCountries = async () => {
    try {
      const response = await apiClient.get('/Countries.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/Currencies.php');
      
      if (response.data.success) {
        setCurrencies(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCurrencies = () => {
    if (!searchTerm.trim()) {
      setFilteredCurrencies(currencies);
    } else {
      const filtered = currencies.filter(currency =>
        currency.currencyname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.monetaryunit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.countryname?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCurrencies(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currencyname.trim()) {
      newErrors.currencyname = 'Currency name is required';
    } else if (formData.currencyname.length > 50) {
      newErrors.currencyname = 'Currency name must be 50 characters or less';
    }
    
    if (formData.symbol && formData.symbol.length > 10) {
      newErrors.symbol = 'Symbol must be 10 characters or less';
    }
    
    if (formData.monetaryunit && formData.monetaryunit.length > 50) {
      newErrors.monetaryunit = 'Monetary unit must be 50 characters or less';
    }
    
    if (formData.noofdecimals < 0 || formData.noofdecimals > 10) {
      newErrors.noofdecimals = 'Decimals must be between 0 and 10';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Currencies.php', {
        ...formData,
        countryid: formData.countryid !== '' ? parseInt(formData.countryid) : null,
        statusbasecurrency: formData.statusbasecurrency ? 1 : 0,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Currency updated successfully!' : 'Currency created successfully!');
        setShowModal(false);
        resetForm();
        fetchCurrencies();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving currency:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Currency name already exists. Please use a different name.');
      } else {
        alert('Error saving currency. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (currency) => {
    setFormData({
      currencyid: currency.currencyid,
      currencyname: currency.currencyname,
      symbol: currency.symbol || '',
      monetaryunit: currency.monetaryunit || '',
      noofdecimals: currency.noofdecimals,
      countryid: currency.countryid || '',
      statusbasecurrency: currency.statusbasecurrency === 1,
      isactive: currency.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id, isBaseCurrency) => {
    if (isBaseCurrency) {
      alert('Cannot delete the base currency. Please set another currency as base first.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this currency?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/Currencies.php', {
        data: { currencyid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Currency deleted successfully!');
        fetchCurrencies();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting currency:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Cannot delete the base currency.');
      } else {
        alert('Error deleting currency. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      currencyid: null,
      currencyname: '',
      symbol: '',
      monetaryunit: '',
      noofdecimals: 2,
      countryid: '',
      statusbasecurrency: false,
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <DollarSign size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            Currencies Master
          </h2>
          <p className="subtitle">Manage currency information and exchange rates</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Currency
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by currency name, symbol, unit, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredCurrencies.length} {filteredCurrencies.length === 1 ? 'currency' : 'currencies'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading currencies...</div>
      ) : filteredCurrencies.length === 0 ? (
        <div className="empty-state">
          <p>No currencies found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Currency Name</th>
                <th>Symbol</th>
                <th>Monetary Unit</th>
                <th>Decimals</th>
                <th>Country</th>
                <th>Base Currency</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCurrencies.map((currency) => (
                <tr key={currency.currencyid}>
                  <td className="font-semibold">{currency.currencyname}</td>
                  <td className="font-mono">{currency.symbol || '-'}</td>
                  <td>{currency.monetaryunit || '-'}</td>
                  <td style={{ textAlign: 'center' }}>{currency.noofdecimals}</td>
                  <td>
                    {currency.countryname ? (
                      <span className="country-badge">
                        {currency.countrycode} - {currency.countryname}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {currency.statusbasecurrency === 1 ? (
                      <span className="status-badge active">BASE</span>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${currency.isactive ? 'active' : 'inactive'}`}>
                      {currency.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(currency.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(currency)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(currency.currencyid, currency.statusbasecurrency === 1)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Currency' : 'Add New Currency'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currencyname">
                    Currency Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="currencyname"
                    value={formData.currencyname}
                    onChange={(e) => setFormData({ ...formData, currencyname: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., US Dollar, Euro"
                    className={errors.currencyname ? 'error' : ''}
                  />
                  {errors.currencyname && (
                    <span className="error-message">{errors.currencyname}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="symbol">Symbol</label>
                  <input
                    type="text"
                    id="symbol"
                    value={formData.symbol}
                    onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                    maxLength="10"
                    placeholder="e.g., $, €, £"
                    className={errors.symbol ? 'error' : ''}
                  />
                  {errors.symbol && (
                    <span className="error-message">{errors.symbol}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="monetaryunit">Monetary Unit</label>
                  <input
                    type="text"
                    id="monetaryunit"
                    value={formData.monetaryunit}
                    onChange={(e) => setFormData({ ...formData, monetaryunit: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., Dollar, Euro, Pound"
                    className={errors.monetaryunit ? 'error' : ''}
                  />
                  {errors.monetaryunit && (
                    <span className="error-message">{errors.monetaryunit}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="noofdecimals">Number of Decimals</label>
                  <input
                    type="number"
                    id="noofdecimals"
                    value={formData.noofdecimals}
                    onChange={(e) => setFormData({ ...formData, noofdecimals: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="10"
                    className={errors.noofdecimals ? 'error' : ''}
                  />
                  {errors.noofdecimals && (
                    <span className="error-message">{errors.noofdecimals}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="countryid">Country</label>
                <select
                  id="countryid"
                  value={formData.countryid}
                  onChange={(e) => setFormData({ ...formData, countryid: e.target.value })}
                >
                  <option value="">-- Select Country (Optional) --</option>
                  {countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname} ({country.countrycode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.statusbasecurrency}
                      onChange={(e) => setFormData({ ...formData, statusbasecurrency: e.target.checked })}
                    />
                    <span>Set as Base Currency</span>
                  </label>
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Note: Only one currency can be set as base currency at a time
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isactive}
                      onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Currencies;